import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navigation from "../components/Navigation";
import HomeOverlay from "../components/HomeOverlay";
import Footer from "../components/Footer";
import { useScrolled } from "../hooks";
import { getGameById, getRelatedGames } from "../data/gamesData";
import "../styles/games.css";

/* ============================================================
   PLAY ICON (SVG)
   ============================================================ */
const PlayIcon = ({ size = 16 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="currentColor"
    aria-hidden="true"
    focusable="false"
    style={{ display: "inline-block", verticalAlign: "middle" }}
  >
    <path d="M8 5.14v13.72c0 .8.87 1.3 1.56.9l11.02-6.86a1.05 1.05 0 0 0 0-1.8L9.56 4.24A1.05 1.05 0 0 0 8 5.14Z" />
  </svg>
);

/* ============================================================
   PLAY ZONE
   ============================================================ */
const PlayZone = ({ game }) => {
  const [launched, setLaunched] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLaunched(false);
    setError(false);
  }, [game.id]);

  const launch = () => {
    if (game.externalUrl) {
      window.open(game.externalUrl, "_blank", "noopener,noreferrer");
      return;
    }
    setLaunched(true);
  };

  if (!launched) {
    return (
      <div className="play-box">
        <div className="play-screen" onClick={launch}>
          <img src={game.img} alt={game.title} />
          <div className="play-overlay">
            <div className="play-btn">
              <PlayIcon size={38} />
            </div>
          </div>
        </div>
        <div className="play-bar">
          <h1>{game.title}</h1>
          <button className="play-now" onClick={launch}>
            <PlayIcon size={16} />
            <span>{game.externalUrl ? "Play on itch.io" : "Play now"}</span>
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="play-box">
        <div className="play-error">
          <i className="fas fa-exclamation-triangle" />
          <p>Could not load the game.</p>
          <code>{game.gamePath}</code>
          <button onClick={() => setLaunched(false)}>Go back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="play-box">
      <div className="play-box-head">
        <span>PLAY ZONE · {game.title}</span>
        <button className="exit-btn" onClick={() => setLaunched(false)}>
          ✕ Exit
        </button>
      </div>
      <div className={game.isVertical ? "frame-vertical" : "frame-wide"}>
        <iframe
          src={game.gamePath}
          title={game.title}
          allowFullScreen
          allow="autoplay; fullscreen; gamepad"
          onError={() => setError(true)}
        />
      </div>
    </div>
  );
};

/* ============================================================
   PAGE
   ============================================================ */
const GameDetail = () => {
  const navigate = useNavigate();
  const { gameId } = useParams();
  const scrolled = useScrolled(500);

  const game = getGameById(gameId);
  const related = game ? getRelatedGames(game.id, 6) : [];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [gameId]);

  if (!game) {
    return (
      <main id="portfolio" className="games-main">
        <HomeOverlay />
        <Navigation />
        <section className="detail">
          <div className="container">
            <button className="back-btn" onClick={() => navigate("/games")}>
              ← Back to library
            </button>
            <div className="lib-empty" style={{ marginTop: "2rem" }}>
              <span>Game Not Found</span>
              <p>This game doesn't exist. Go back to the library to pick one.</p>
            </div>
          </div>
        </section>
        <Footer />
      </main>
    );
  }

  return (
    <main id="portfolio" className={`games-main${scrolled ? " scrolled" : ""}`}>
      <HomeOverlay />
      <Navigation />

      <section className="detail">
        <div className="container">
          <button className="back-btn" onClick={() => navigate("/games")}>
            ← Back to library
          </button>

          <div className="detail-layout">
            {/* ============ CỘT TRÁI ============ */}
            <div className="detail-main">
              <PlayZone game={game} />

              <div className="desc-box">
                <div className="desc-label">About this game</div>
                <ul className="desc-list">
                  {game.points.map((p, i) => (
                    <li key={i}>{p}</li>
                  ))}
                </ul>
                <div className="desc-meta">
                  <span><b>Role:</b> {game.role}</span>
                  <span><b>Date:</b> {game.duration}</span>
                  <span><b>Tech:</b> {game.tech}</span>
                </div>
                {game.github && (
                  <a
                    href={game.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="github-games-link"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M12,2.247A10,10,0,0,0,8.837,21.735c.5.094.684-.215.684-.481,0-.237-.009-.867-.013-1.7-2.781.6-3.368-1.342-3.368-1.342A2.648,2.648,0,0,0,5.027,16.75c-.905-.62.071-.608.071-.608a2.1,2.1,0,0,1,1.531,1.03A2.131,2.131,0,0,0,9.542,18a2.129,2.129,0,0,1,.633-1.337c-2.221-.25-4.555-1.11-4.555-4.942A3.859,3.859,0,0,1,6.649,9.042,3.558,3.558,0,0,1,6.737,6.4s.837-.268,2.75,1.025a9.415,9.415,0,0,1,5,0c1.9-1.293,2.737-1.025,2.737-1.025a3.652,3.652,0,0,1,.1,2.647,3.87,3.87,0,0,1,1.025,2.683c0,3.842-2.337,4.687-4.562,4.933a2.4,2.4,0,0,1,.675,1.85c0,1.339-.013,2.414-.013,2.739,0,.262.175.575.688.475A9.988,9.988,0,0,0,12,2.247" />
                    </svg>
                    <span>{game.githubLabel || "View on GitHub"}</span>
                  </a>
                )}
              </div>
            </div>

            {/* ============ CỘT PHẢI ============ */}
            <aside className="detail-side">
              <div className="side-card">
                <div className="side-label">How to play</div>
                <ul className="howto-list">
                  {game.howToPlay.map((step, i) => (
                    <li key={i}>
                      <span className="step">{i + 1}</span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="side-card">
                <div className="side-label">More like this</div>
                {related.length === 0 ? (
                  <p className="side-empty">No related games yet.</p>
                ) : (
                  <div className="related-list">
                    {related.map((r) => (
                      <div
                        key={r.id}
                        className="related-item"
                        onClick={() => navigate(`/games/${r.id}`)}
                      >
                        <div className="related-thumb">
                          <img src={r.img} alt={r.title} loading="lazy" />
                        </div>
                        <div className="related-info">
                          <span className="rt">{r.title}</span>
                          <span className="rg">{r.genre}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default GameDetail;